import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['draft', 'active', 'archived'];
const CHANNELS = ['email', 'sms', 'push', 'in_app'];

function formatVarsJson(obj) {
  if (obj == null) return '{}';
  try {
    return JSON.stringify(obj, null, 2);
  } catch (_e) {
    return '{}';
  }
}

export default function PlatformNotificationTemplateFormPage() {
  const { templateId } = useParams();
  const isEdit = Boolean(templateId);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [templateName, setTemplateName] = useState('');
  const [templateCode, setTemplateCode] = useState('');
  const [channel, setChannel] = useState('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [varsText, setVarsText] = useState('{}');
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/notification-templates/${templateId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const t = json.template;
      setTemplateName(t.template_name || '');
      setTemplateCode(t.template_code || '');
      setChannel(t.channel || 'email');
      setSubject(t.subject || '');
      setBody(t.body || '');
      setVarsText(formatVarsJson(t.variables_json));
      setStatus(t.status || 'draft');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, templateId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuper) {
    return <Navigate to={isEdit ? `/platform/notification-templates/${templateId}` : '/platform/notification-templates'} replace />;
  }

  const save = async (e) => {
    e.preventDefault();
    let variables_json;
    try {
      const parsed = JSON.parse(varsText.trim() || '{}');
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setError('Variables must be a JSON object');
        return;
      }
      variables_json = parsed;
    } catch (_e) {
      setError('Variables JSON is invalid');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        template_name: templateName.trim(),
        channel,
        body: body.trim(),
        variables_json,
        status
      };
      if (templateCode.trim()) payload.template_code = templateCode.trim().toLowerCase();
      if (channel === 'email') {
        payload.subject = subject.trim();
      } else {
        payload.subject = subject.trim() || null;
      }

      if (isEdit) {
        const res = await platformApiFetch(`/api/platform/notification-templates/${templateId}`, {
          method: 'PATCH',
          body: payload
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Save failed');
        navigate(`/platform/notification-templates/${templateId}`);
      } else {
        const res = await platformApiFetch('/api/platform/notification-templates', {
          method: 'POST',
          body: payload
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Create failed');
        navigate(`/platform/notification-templates/${json.template.id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {isEdit ? 'Edit global template' : 'New global template'}
      </Typography>
      <Button variant="outlined" component={Link} to={isEdit ? `/platform/notification-templates/${templateId}` : '/platform/notification-templates'} sx={{ mb: 2 }}>
        Cancel
      </Button>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, maxWidth: 900 }}>
        <Box component="form" onSubmit={save}>
          <TextField fullWidth required label="Template name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} margin="normal" />
          <TextField
            fullWidth
            label="Template code"
            value={templateCode}
            onChange={(e) => setTemplateCode(e.target.value)}
            margin="normal"
            helperText="Unique among global templates. Leave blank on create to derive from name."
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Channel</InputLabel>
            <Select label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
              {CHANNELS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} margin="normal" required={channel === 'email'} helperText={channel === 'email' ? 'Required for email' : 'Optional'} />
          <TextField fullWidth required label="Body" value={body} onChange={(e) => setBody(e.target.value)} margin="normal" multiline minRows={10} />
          <TextField
            fullWidth
            label="Variables (JSON object)"
            value={varsText}
            onChange={(e) => setVarsText(e.target.value)}
            margin="normal"
            multiline
            minRows={4}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={saving || !templateName.trim() || !body.trim() || (channel === 'email' && !subject.trim())}>
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
